import React, { Component } from 'react'
import {
  Editor,
  EditorState,
  RichUtils,
  getDefaultKeyBinding,
  KeyBindingUtil
} from 'draft-js'
import { stateToHTML } from 'draft-js-export-html'
import { stateFromHTML } from 'draft-js-import-html'
import { keys, sortBy, flatMap, map, mapValues, pickBy, uniq, find, filter, includes } from 'libraries/lodash'
import { forkJoin } from 'rxjs'
import SaveOrDiscard from './SaveOrDiscard'
import {
  editTakeoverParagraph,
  deleteTakeoverParagraph,
  createTakeoverParagraph,
  editTakeoverPrefix
} from 'services/LegislationService'
import { toast } from 'react-toastify'
import notificationToast from 'helpers/notificationToast'
import ErrorIcon from '@material-ui/icons/Error'
import WarningIcon from '@material-ui/icons/Warning'
import CheckIcon from '@material-ui/icons/CheckCircle'

const { hasCommandModifier } = KeyBindingUtil

export default class ContentEditor extends Component {
  constructor (props) {
    super(props)
    const { node: { paragraphs } } = props
    this.originalParagraphs = this.generateParagraphs(paragraphs)
    this.state = {
      saving: false,
      paragraphs: this.originalParagraphs
    }
  }

  notificationTypes = {
    ALERT: 'alert',
    SUCCESS: 'success',
    ERROR: 'error'
  }

  generateParagraphs = paragraphs =>
    paragraphs.reduce((map, paragraph) => {
      map[paragraph.uuid] = {
        ...paragraph,
        indentLevel: Number(paragraph.indentLevel),
        indexNumber: Number(paragraph.indexNumber),
        textPrefixes: this.generateTextPrefixes(paragraph.textPrefixes),
        editorState: EditorState.createWithContent(
          stateFromHTML(paragraph.text)
        )
      }
      return map
    }, {})

  generateTextPrefixes = textPrefixes =>
    textPrefixes.map(textPrefix => ({
      ...textPrefix,
      editorState: EditorState.createWithContent(
        stateFromHTML(textPrefix.text)
      )
    }))

  postChanges = changes => {
    const requests = flatMap(changes, (_, type) => {
      return flatMap(changes[type], (value, key) => {
        if (type === 'TEXT_PREFIXES') {
          // if (key === 'DELETE') {
          //   return value.map(deleteTextPrefix)
          // }
          // if (key === 'CREATE') {
          //   return value.map(createTextPrefix)
          // }
          return value.map(editTakeoverPrefix)
        }
        if (key === 'DELETE') {
          return value.map(deleteTakeoverParagraph)
        }
        if (key === 'CREATE') {
          return value.map(createTakeoverParagraph)
        }
        return value.map(editTakeoverParagraph)
      })
    })
    return forkJoin(...requests)
  }

  findChanges = () => {
    const { originalParagraphs } = this
    const { paragraphs } = this.state
    const look = (changes, type, original, current) => {
      if (!current) {
        changes[type].DELETE.push(original)
        return changes
      }
      if (
        (!original && current) ||
        !editorStateEqual(original.editorState, current.editorState) ||
        original.indentLevel !== current.indentLevel
      ) {
        const change = {
          ...current,
          text: stripTags(stateToHTML(current.editorState.getCurrentContent()), '<p>')
        }
        changes[type][current.id ? 'UPDATE' : 'CREATE'].push(
          type === 'PARAGRAPHS'
            ? {
                ...change,
                indentLevel: current.indentLevel
              }
            : change
        )
      }
      const uuids = type === 'PARAGRAPHS' ? uniq(original.textPrefixes.map(({ uuid }) => uuid).concat(current.textPrefixes.map(({ uuid }) => uuid))) : null
      return type === 'PARAGRAPHS'
        ? uuids.reduce((changes, uuid) => {
          const textPrefix = find(current.textPrefixes, textPrefix => textPrefix.uuid === uuid)
          const originalTextPrefix = find(original.textPrefixes, textPrefix => textPrefix.uuid === uuid)
          return look(changes, 'TEXT_PREFIXES', originalTextPrefix, textPrefix)
        }, changes)
        : changes
    }

    return (changes, uuid) => {
      const paragraph = paragraphs[uuid]
      const original = originalParagraphs[uuid]
      return look(changes, 'PARAGRAPHS', original, paragraph)
    }
  }

  createParagraph = uuid => () => {
    const { paragraphs } = this.state
    const sectionId = this.props.node.id
    const precedingParagraph = find(paragraphs, p => p.uuid === uuid)
    const newIndexNumber = precedingParagraph.indexNumber + 1
    const newUuid = guid()
    const newParagraph = {
      id: null,
      uuid: newUuid,
      textPrefixes: [],
      text: '',
      sectionId,
      indentLevel: 0,
      indexNumber: newIndexNumber,
      editorState: EditorState.createWithContent(
        stateFromHTML('')
      )
    }
    this.setState({
      paragraphs: {
        ...mapValues(paragraphs, p => p.indexNumber >= newIndexNumber ? { ...p, indexNumber: p.indexNumber + 1 } : p),
        [newUuid]: newParagraph
      }
    })
  }

  destroyParagraph = uuid => () => {
    const { paragraphs } = this.state
    const { indexNumber } = find(paragraphs, p => p.uuid === uuid)
    this.setState({
      paragraphs: mapValues(
        pickBy(paragraphs, p => p.uuid !== uuid),
        p => p.indexNumber >= indexNumber ? { ...p, indexNumber: p.indexNumber - 1 } : p
      )
    })
  }

  createTextPrefix = uuid => () => {
    const { paragraphs } = this.state
    const paragraph = paragraphs[uuid]
    const indexNumber = Math.max(paragraph.textPrefixes.map(({ indexNumber }) => indexNumber)) + 1
    const newUuid = guid()
    const textPrefix = {
      id: null,
      indexNumber,
      paragraphId: paragraph.id,
      uuid: newUuid,
      text: '',
      editorState: EditorState.createWithContent(
        stateFromHTML('')
      )
    }
    this.setState({
      paragraphs: {
        ...paragraphs,
        [uuid]: {
          ...paragraph,
          textPrefixes: paragraph.textPrefixes.concat(textPrefix)
        }
      }
    })
  }

  destroyTextPrefix = uuid => () => {
    const { paragraphs } = this.state
    const textPrefix = find(
      flatMap(paragraphs, ({ textPrefixes }) => textPrefixes),
      textPrefix => textPrefix.uuid === uuid)
    const paragraph = find(paragraphs, ({ id }) => id === textPrefix.paragraphId)
    const { indexNumber } = textPrefix
    const newTextPrefixes = filter(
      paragraph.textPrefixes, textPrefix => textPrefix.uuid !== uuid)
      .map(textPrefix =>
        textPrefix.indexNumber >= indexNumber
          ? {
              ...textPrefix,
              indexNumber: textPrefix.indexNumber - 1
            }
          : textPrefix
      )
    this.setState({
      paragraphs: {
        ...paragraphs,
        [paragraph.uuid]: {
          ...paragraph,
          textPrefixes: newTextPrefixes
        }
      }
    })
  }

  notify = type => {
    const { ALERT, ERROR } = this.notificationTypes
    switch (type) {
      case ALERT:
        notificationToast(<WarningIcon />, toast.TYPE.WARNING, 'No changes have been made!')
        break
      case ERROR:
        notificationToast(<ErrorIcon />, toast.TYPE.ERROR, 'Error', 'bg-indian-red white')
        break
      default:
        notificationToast(<CheckIcon />, toast.TYPE.SUCCESS, 'Saved!', 'bg-dark-sea-green white')
    }
  }

  onSave = () => {
    this.setState({ saving: true }, () => {
      const uuids = uniq(keys(this.state.paragraphs).concat(keys(this.originalParagraphs)))
      const changes = uuids.reduce(
        this.findChanges(), {
          PARAGRAPHS: { UPDATE: [], DELETE: [], CREATE: [] },
          TEXT_PREFIXES: { UPDATE: [], DELETE: [], CREATE: [] }
        }
      )
      const changesMade = flatMap(keys(changes), key => flatMap(keys(changes[key]), innerKey => changes[key][innerKey])).length
      if (changesMade) {
        this.postChanges(changes).subscribe(responses => {
          const success = responses.reduce(
            (acc, response) => response.success && acc,
            true
          )
          this.notify(success ? this.notificationTypes.SUCCESS : this.notificationTypes.ERROR)
          this.setState({ saving: false })
          if (success) {
            // NOTE: there is probably a better way
            window.location.reload()
          }
        })
      } else {
        this.notify(this.notificationTypes.ALERT)
      }
    })
  }

  setEditorState = (uuid, editTextPrefix = false) => editorState => {
    const { paragraphs } = this.state
    const textPrefix = editTextPrefix
      ? find(
        flatMap(paragraphs, ({ textPrefixes }) => textPrefixes),
        textPrefix => textPrefix.uuid === uuid)
      : false
    const paragraph = textPrefix ? find(paragraphs, ({ id }) => id === textPrefix.paragraphId) : paragraphs[uuid]
    const { textPrefixes } = paragraph
    const newParagraph = {
      ...paragraph,
      editorState: editTextPrefix ? paragraph.editorState : editorState,
      textPrefixes: editTextPrefix
        ? textPrefixes.map(textPrefix => {
          return textPrefix.uuid === uuid ? { ...textPrefix, editorState } : textPrefix
        })
        : textPrefixes
    }
    this.setState({
      paragraphs: {
        ...paragraphs,
        [paragraph.uuid]: newParagraph
      }
    })
  }

  setIndentLevel = uuid => indentLevel => {
    const { paragraphs } = this.state
    const paragraph = paragraphs[uuid]
    this.setState({ paragraphs: { ...paragraphs, [uuid]: { ...paragraph, indentLevel } } })
  }

  wrap = (child, uuid, indentLevel) => {
    if (indentLevel > 0) {
      const wrapped = <span className='fc-p' key={uuid}>{child}</span>
      return this.wrap(wrapped, uuid, indentLevel - 1)
    }
    return child
  }

  renderTextPrefixes ({ id, textPrefixes }) {
    return textPrefixes.length
      ? (
        <span className='fc-reference' style={{ minWidth: '20px' }}>
          {textPrefixes.map((textPrefix, index) => {
            const className = `fc-stacked-reference-${textPrefixes.length - index}`
            const { uuid, editorState } = textPrefix
            return textPrefix
              ? (
                <span key={uuid} className={className} style={{ minWidth: '20px' }}>
                  <BasicEditor
                    editorState={editorState}
                    setEditorState={this.setEditorState(uuid, true)}
                    destroy={this.destroyTextPrefix(uuid)}
                  />
                </span>
                )
              : null
          })}
        </span>
        )
      : null
  }

  renderParagraphs (paragraphs) {
    return map(paragraphs, paragraph => {
      const { uuid, indentLevel, editorState } = paragraph
      const isTable = includes(paragraph.text, '<table>')
      const indent = paragraph.textPrefixes.length > 0 ? indentLevel + paragraph.textPrefixes.length : indentLevel
      const textPrefixes = this.renderTextPrefixes(paragraph)
      const editor = (
        <span key={uuid} className='fc-p'>
          {textPrefixes}
          <ExtendedEditor
            {...paragraph}
            editorState={editorState}
            setEditorState={this.setEditorState(uuid)}
            setIndentLevel={this.setIndentLevel(uuid)}
            createTextPrefix={this.createTextPrefix(uuid)}
            create={this.createParagraph(uuid)}
            destroy={this.destroyParagraph(uuid)}
          />
        </span>
      )
      // Tables can't be edited atm.
      return (
        isTable
          ? this.wrap(<span key={uuid} className='fc-p' dangerouslySetInnerHTML={{ __html: paragraph.text }} />, uuid, indent)
          : this.wrap(editor, uuid, indent)
      )
    })
  }

  render () {
    const { onDiscard } = this.props
    const { paragraphs } = this.state
    return (
      <div className='content-editor content-text'>
        {this.renderParagraphs(sortBy(paragraphs, p => p.indexNumber))}
        <SaveOrDiscard handleOnSave={this.onSave} onDiscard={onDiscard} />
      </div>
    )
  }
}

class BasicEditor extends Component {
  keyMap = {
    DELETE: 8,
    ENTER: 13,
    I: 73,
    B: 66,
    SQUARE_BRACKET_RIGHT: 221,
    SQUARE_BRACKET_LEFT: 219,
    DASH: 189
  }

  handleOnChange = editorState => this.props.setEditorState(editorState)

  onHandleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command)
    return newState && this.handleOnChange(newState)
  }

  keyBindingFn = e => {
    const { keyMap } = this
    const { keyCode } = e
    const { editorState, destroy } = this.props
    if (keyCode === keyMap.DELETE) {
      const hasContent = editorState.getCurrentContent().hasText()
      if (!hasContent) {
        return destroy()
      }
    }
    if (hasCommandModifier(e) && keyCode !== keyMap.B && keyCode !== keyMap.I) {
      return
    }
    return getDefaultKeyBinding(e)
  }

  render () {
    return (
      <div className='content-editor__editor'>
        <Editor
          editorState={this.props.editorState}
          handleKeyCommand={this.onHandleKeyCommand}
          onChange={this.handleOnChange}
          keyBindingFn={this.keyBindingFn}
        />
      </div>
    )
  }
}

class ExtendedEditor extends BasicEditor {
  setIndentLevel = increase => {
    const { setIndentLevel, indentLevel } = this.props
    const newIndentLevel = increase ? indentLevel + 1 : indentLevel - 1 > 0 ? indentLevel - 1 : 0
    setIndentLevel(newIndentLevel)
  }

  keyBindingFn = e => {
    const { keyMap } = this
    const { keyCode } = e
    const { editorState, createTextPrefix, create, destroy } = this.props
    const hasContent = editorState.getCurrentContent().hasText()
    switch (keyCode) {
      case keyMap.ENTER:
        return create()
      case keyMap.DELETE:
        if (!hasContent) {
          return destroy()
        }
        break
      case keyMap.SQUARE_BRACKET_LEFT:
        if (hasCommandModifier(e)) {
          e.preventDefault()
          return this.setIndentLevel(keyCode === keyMap.SQUARE_BRACKET_RIGHT)
        }
        break
      case keyMap.SQUARE_BRACKET_RIGHT:
        if (hasCommandModifier(e)) {
          e.preventDefault()
          return this.setIndentLevel(keyCode === keyMap.SQUARE_BRACKET_RIGHT)
        }
        break
      case keyMap.DASH:
        if (hasCommandModifier(e)) {
          e.preventDefault()
          return createTextPrefix()
        }
        break
      case keyMap.B:
        break
      case keyMap.I:
        break
      default:
        return getDefaultKeyBinding(e)
    }
  }

  render () {
    return (
      <div className='content-editor__editor'>
        <Editor
          editorState={this.props.editorState}
          handleKeyCommand={this.onHandleKeyCommand}
          onChange={this.handleOnChange}
          keyBindingFn={this.keyBindingFn}
        />
      </div>
    )
  }
}

const stripTags = (str, tag) => {
  const closingTag = `${tag.slice(0, 1)}/${tag.slice(1, tag.length)}`
  return str.replace(tag, '').replace(closingTag, '')
}

const guid = () => {
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1)
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`
}

const editorStateEqual = (editorStateOne, editorStateTwo) => editorStateOne.getCurrentContent().equals(editorStateTwo.getCurrentContent())
